import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardMedia,
  CircularProgress,
  Alert,
  Chip,
  Button,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { GiMedicinePills } from 'react-icons/gi';
import { MdOutlineImageNotSupported } from 'react-icons/md';
import userService from '../services/userService';

const RelatedProducts = ({ productId, productType = 'product', title }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  
  const INITIAL_LIMIT = 10;

  useEffect(() => {
    if (productId) {
      fetchRelatedProducts();
    }
  }, [productId, productType]);

  const fetchRelatedProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (productType === 'product') {
        response = await userService.getSameSaltProducts(productId);
      } else {
        response = await userService.getSameCategoryOTCs(productId);
      }

      if (response.data) {
        // Handle edge cases
        if (response.data.message) {
          // Product without salt composition or OTC without category
          setProducts([]);
        } else if (response.data.results && Array.isArray(response.data.results)) {
          setProducts(response.data.results);
          // Store category name for OTC products
          if (productType === 'otc' && response.data.category) {
            setCategoryName(response.data.category.name);
          }
        } else {
          setProducts([]);
        }
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Error fetching related products:', err);
      setError('Failed to load related products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const getProductImage = (images, isOTC = false) => {
    if (!images) {
      return null;
    }

    if (Array.isArray(images)) {
      return images[0] || null;
    } else {
      // Handle string format
      if (isOTC) {
        const imageNames = images.split(',').map(img => img.trim()).filter(img => img);
        return imageNames[0] ? `/assets/images_1mg_otc/${imageNames[0]}` : null;
      } else {
        const imageNames = images.split(',').map(img => img.trim()).filter(img => img);
        return imageNames[0] ? `/assets/Drugs Images/${imageNames[0]}` : null;
      }
    }
  };

  const handleProductClick = (id) => {
    if (productType === 'product') {
      navigate(`/product/${id}`);
    } else {
      navigate(`/otc/${id}`);
    }
  };

  const handleViewMore = () => {
    if (productType === 'product') {
      navigate(`/product/${productId}/related`);
    } else {
      navigate(`/otc/${productId}/related`);
    }
  };

  const getDefaultTitle = () => {
    if (title) return title;
    if (productType === 'product') {
      return 'Similar Products';
    } else {
      return categoryName ? `More from ${categoryName}` : 'Similar Products';
    }
  };

  // Get first 10 products for carousel
  const displayedProducts = products.slice(0, INITIAL_LIMIT);

  // Don't render if loading, error, or no products
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!products || products.length === 0) {
    return null; // Don't render anything if no products
  }

  const hasMoreProducts = products.length > INITIAL_LIMIT;

  return (
    <Box sx={{ mt: 6, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {getDefaultTitle()}
        </Typography>
        {hasMoreProducts && (
          <Button
            variant="text"
            onClick={handleViewMore}
            sx={{
              textTransform: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          >
            View All ({products.length})
          </Button>
        )}
      </Box>

      {/* Horizontal Scrollable Carousel */}
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          overflowY: 'hidden',
          gap: 2,
          pb: 2,
          scrollBehavior: 'smooth',
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'grey.100',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'grey.400',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'grey.500',
            },
          },
        }}
      >
        {displayedProducts.map((product) => (
          <Box
            key={product.id}
            sx={{
              minWidth: { xs: '200px', sm: '240px', md: '280px' },
              maxWidth: { xs: '200px', sm: '240px', md: '280px' },
              flexShrink: 0,
            }}
          >
            <Card
              onClick={() => handleProductClick(product.id)}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
                  borderColor: 'primary.main',
                },
              }}
            >
              {/* Product Image */}
              <Box
                sx={{
                  width: '100%',
                  height: { xs: '140px', sm: '160px', md: '180px' },
                  backgroundColor: 'grey.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                }}
              >
                {getProductImage(product.images, productType === 'otc') ? (
                  <CardMedia
                    component="img"
                    image={getProductImage(product.images, productType === 'otc')}
                    alt={product.name}
                    sx={{
                      objectFit: 'contain',
                      maxWidth: '100%',
                      maxHeight: '100%',
                    }}
                  />
                ) : (
                  productType === 'product' ? (
                    <GiMedicinePills 
                      size={80} 
                      color={theme.palette.primary.main} 
                    />
                  ) : (
                    <MdOutlineImageNotSupported 
                      size={80} 
                      color={theme.palette.primary.main} 
                    />
                  )
                )}
              </Box>

              {/* Product Info */}
              <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Product Name */}
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' },
                    fontWeight: 500,
                    color: 'text.primary',
                    mb: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    minHeight: '2.5em',
                  }}
                >
                  {product.name}
                </Typography>

                {/* Manufacturer */}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.8rem' },
                    color: 'text.secondary',
                    mb: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {product.manufacturer}
                </Typography>

                {/* Category/Sub-category for OTC */}
                {productType === 'otc' && (
                  <>
                    {product.category && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' },
                          color: 'text.secondary',
                          mb: 0.5,
                        }}
                      >
                        {product.category}
                      </Typography>
                    )}
                    {product.sub_category && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' },
                          color: 'primary.main',
                          mb: 1,
                        }}
                      >
                        {product.sub_category}
                      </Typography>
                    )}
                  </>
                )}

                {/* Salt Composition for Products */}
                {productType === 'product' && product.salt_composition && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.75rem' },
                      color: 'text.secondary',
                      mb: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {product.salt_composition}
                  </Typography>
                )}

                {/* Price Section */}
                <Box
                  sx={{
                    mt: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                      color: 'primary.main',
                      fontWeight: 600,
                    }}
                  >
                    ₹{product.best_price || product.price}
                  </Typography>
                  {product.best_price && product.best_price !== product.price && (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: 'line-through',
                          color: 'text.secondary',
                          fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' },
                        }}
                      >
                        ₹{product.price}
                      </Typography>
                      {product.discount_percent && (
                        <Chip
                          label={`${product.discount_percent}% OFF`}
                          color="success"
                          size="small"
                          sx={{
                            fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                            height: { xs: '20px', sm: '22px', md: '24px' },
                          }}
                        />
                      )}
                    </>
                  )}
                </Box>

                {/* Prescription Required Badge */}
                {product.prescription_required && (
                  <Chip
                    label="Prescription Required"
                    color="warning"
                    size="small"
                    sx={{
                      mt: 1,
                      fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.7rem' },
                      height: { xs: '18px', sm: '20px', md: '22px' },
                    }}
                  />
                )}
              </Box>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RelatedProducts;

